/**
 * Разбор ссылок в тексте сообщения (CC §12.5, CLIENT_WEB.md §11.4 п. 4).
 *
 * Текст сообщения — plain text: HTML и Markdown НЕ интерпретируются, ссылки
 * выделяет собственный парсер. Перед открытием внешней ссылки показывается
 * предупреждение C-22 с РЕАЛЬНЫМ доменом — обязательно для подозрительных и
 * маскированных, и клиент не полагается на то, как ссылка выглядит в тексте.
 */

/** Латинские буквы, у которых есть визуальные двойники в других алфавитах. */
const CONFUSABLE_LATIN = /[a-z]/i
/** Кириллица и греческий — источник омоглифов вроде `аpple.com` с русской «а». */
const CONFUSABLE_NON_LATIN = /[Ѐ-ӿͰ-Ͽ]/

/** Схемы, которые вообще могут быть ссылкой в тексте. */
const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/gi

export type LinkRisk =
  /** Обычная внешняя ссылка. */
  | 'normal'
  /** Подозрительная: смешанные алфавиты, punycode, вход в URL внутри URL. */
  | 'suspicious'

export interface ParsedLink {
  /** Как ссылка выглядит в тексте. */
  raw: string
  /** Нормализованный абсолютный URL — то, что действительно откроется. */
  href: string
  /** Реальный домен для показа пользователю (C-22). */
  host: string
  risk: LinkRisk
  /** Путь на собственный origin — открывается экраном приложения, а не браузером. */
  internal: boolean
}

export type TextPart = { kind: 'text'; value: string } | { kind: 'link'; link: ParsedLink }

function hostRisk(url: URL, raw: string): LinkRisk {
  const host = url.hostname

  // `xn--` — punycode: под ним может скрываться что угодно, показываем как есть.
  if (host.includes('xn--')) return 'suspicious'

  // Смешанные алфавиты в одном домене — классический омоглиф.
  if (CONFUSABLE_LATIN.test(host) && CONFUSABLE_NON_LATIN.test(host)) return 'suspicious'
  if (CONFUSABLE_NON_LATIN.test(host)) return 'suspicious'

  // Учётные данные в URL и вложенный адрес: `https://bank.ru@evil.com`.
  if (url.username || url.password) return 'suspicious'
  if (/https?:\/\/[^\s]*https?:\/\//i.test(raw)) return 'suspicious'

  // `@` в тексте ссылки до домена — то же самое, но до нормализации.
  if (raw.includes('@') && raw.indexOf('@') < raw.length - host.length) return 'suspicious'

  return 'normal'
}

export function parseLink(raw: string): ParsedLink | null {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return null
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

  return {
    raw,
    href: url.toString(),
    host: url.hostname,
    risk: hostRisk(url, raw),
    internal: url.origin === globalThis.location?.origin,
  }
}

/**
 * Делит текст на обычные куски и ссылки. Ничего не экранирует и не вставляет
 * в DOM: возвращает данные, а отрисовка остаётся текстовыми узлами React.
 */
export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = []
  let lastIndex = 0

  for (const match of text.matchAll(URL_PATTERN)) {
    const raw = match[0]
    const index = match.index ?? 0

    // Хвостовая пунктуация к ссылке не относится: «…смотри https://a.ru.»
    const trimmed = raw.replace(/[),.;:!?»"']+$/, '')
    const link = parseLink(trimmed)

    if (index > lastIndex) parts.push({ kind: 'text', value: text.slice(lastIndex, index) })

    if (link) {
      parts.push({ kind: 'link', link })
      if (trimmed.length < raw.length) parts.push({ kind: 'text', value: raw.slice(trimmed.length) })
    } else {
      parts.push({ kind: 'text', value: raw })
    }

    lastIndex = index + raw.length
  }

  if (lastIndex < text.length) parts.push({ kind: 'text', value: text.slice(lastIndex) })
  return parts
}
